import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogSlugs, ProtectedPaths } from "@/app/vars/Routes";

export const prospectListsArticle: BlogArticle = {
  key: BlogArticleKey.PROSPECT_LISTS,
  slug: BlogSlugs.PROSPECT_LISTS,
  categoryId: "03",
  date: "2026-09-02",
  readTime: 8,
  locales: {
    fr: {
      title: "Acheter une liste de prospects B2B : ce que tu obtiens",
      excerpt:
        "Une liste achetée est une photo prise un jour donné, sur un critère qui ne dit pas si la personne a besoin de toi. Voilà ce que tu paies, et ce que ça coûte après.",
      description:
        "Ce que contient vraiment une liste achetée, pourquoi elle vieillit plus vite qu'on croit, et ce que change le fait de la construire depuis tes appels.",
      ctaTitle: "Construis ta liste au lieu de l'acheter",
      ctaText:
        "On tire tes critères de ciblage de ce que disent tes clients, puis on cherche les profils qui y correspondent vraiment. Sans engagement.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Acheter une liste est tentant pour une raison simple : c'est la seule façon d'avoir mille noms cet après-midi. Tout le reste demande des semaines.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le problème n'est pas que ces listes soient mauvaises. C'est qu'elles répondent à une question que tu ne t'es pas posée : elles te disent qui existe, jamais qui a besoin de toi.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Ce que tu achètes vraiment",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une liste est une photographie. Quelqu'un a parcouru des profils publics et des sites d'entreprises à une date donnée, a retenu ceux dont l'intitulé de poste correspondait à ta demande, et t'a vendu le résultat.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Deux choses en découlent, et ce sont elles qui expliquent les résultats décevants. La photo vieillit dès qu'elle est prise. Et le critère de tri, l'intitulé de poste, est le plus faible de tous : il dit ce que quelqu'un fait, pas ce qui lui pose problème en ce moment.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Une liste te dit qui existe. Elle ne dit jamais qui a un problème que tu sais résoudre, ni maintenant.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Les trois coûts qu'on ne voit pas à l'achat",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**L'usure.** Les gens changent de poste, d'entreprise, d'adresse. Une liste ne se dégrade pas d'un coup, elle se dégrade tous les mois, et personne ne te prévient quand elle passe sous le seuil de l'utile.",
            "**L'absence d'intention.** Rien dans la liste ne dit pourquoi écrire à cette personne aujourd'hui plutôt que dans un an. Tu vas donc écrire à tout le monde la même chose, ce qui est exactement ce qui fait chuter les réponses.",
            "**La responsabilité.** Dès que tu utilises ces données, tu deviens responsable du traitement pour l'usage que tu en fais : informer les personnes, permettre l'opposition, être capable de dire d'où vient la donnée. Le vendeur ne porte pas ça à ta place.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "En prospection B2B, écrire à quelqu'un au sujet de sa fonction professionnelle ne demande pas son accord préalable. Cela ne dispense ni de l'informer, ni de lui laisser un moyen simple de refuser, dans chaque message. Une liste achetée ne t'apporte aucune de ces deux garanties.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Liste achetée, liste construite",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Ce que donne une liste achetée",
            items: [
              "Mille noms, triés sur un intitulé de poste",
              "Aucune idée de ce qui les préoccupe",
              "Un message identique pour tout le monde",
              "Une provenance que tu ne peux pas expliquer",
            ],
          },
          good: {
            title: "Ce que donne une liste construite",
            items: [
              "Moins de noms, choisis sur une situation",
              "Une raison d'écrire, propre à chacun",
              "Des mots repris de tes propres clients",
              "Une origine que tu assumes et peux justifier",
            ],
          },
        },
        {
          type: ArticleBlockType.H2,
          text: "Le test à faire avant d'envoyer quoi que ce soit",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Prends une ligne de ta liste au hasard et réponds à une seule question : pourquoi cette personne, et pourquoi aujourd'hui. Si la réponse est son intitulé de poste, tu n'as pas de raison d'écrire, tu as une adresse.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ce test élimine la plupart des listes achetées en dix secondes, et il élimine aussi beaucoup de listes construites à la main. Il ne dit pas que le volume est inutile ; il dit qu'un volume sans raison d'écrire produit du volume de silence.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Ce que tu as déjà et qui vaut mieux",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Tes appels contiennent la matière qu'aucun fournisseur ne peut te vendre : la situation dans laquelle se trouvaient tes clients quand ils ont acheté, ce qui les bloquait, et les mots qu'ils ont employés pour le dire.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "C'est de là que sortent de vrais critères de recherche. Non pas des intitulés de poste, mais des signaux : une équipe qui vient de grossir, un outil qu'on annonce avoir abandonné, une responsabilité qui change. Une liste triée là-dessus est plus courte, et chaque ligne porte sa raison d'être contactée.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Des critères tirés de tes appels, puis les profils qui collent",
          text: "La recherche de prospects de Meidly construit tes critères de ciblage à partir de ce que disent tes clients, puis passe les profils au crible de ces critères plutôt que d'un intitulé de poste.",
          linkLabel: "Découvrir la recherche de prospects",
          linkPath: ProtectedPaths.PROSPECT_SEARCH,
        },
        {
          type: ArticleBlockType.H2,
          text: "Quand une liste achetée se défend quand même",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "Pour cartographier un marché que tu ne connais pas encore, sans écrire à personne.",
            "Pour vérifier la taille d'un segment avant d'y investir du temps.",
            "Pour retrouver des entreprises précises dont tu as déjà décidé qu'elles t'intéressaient.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Dans ces trois cas, la liste sert à comprendre, pas à contacter. C'est l'usage où elle est honnête, et personne ne la vend comme ça.",
        },
      ],
      faq: [
        {
          question: "Acheter une liste de prospects B2B est-il légal en France ?",
          answer:
            "Écrire à une personne au sujet de sa fonction professionnelle ne demande pas son accord préalable en B2B. En revanche, tu dois l'informer, indiquer d'où vient la donnée et lui laisser un moyen simple de s'opposer dans chaque message. En utilisant la liste, tu deviens responsable du traitement pour l'usage que tu en fais.",
        },
        {
          question: "Pourquoi une liste achetée donne-t-elle si peu de réponses ?",
          answer:
            "Parce qu'elle est triée sur l'intitulé de poste, qui dit ce qu'une personne fait et jamais ce qui lui pose problème. Sans raison d'écrire propre à chacun, le message est le même pour tous, et c'est ce qui fait chuter le taux de réponse bien plus que la qualité des adresses.",
        },
        {
          question: "À quelle vitesse une liste se périme-t-elle ?",
          answer:
            "Elle ne se périme pas d'un coup, elle s'use en continu, au rythme des changements de poste et d'entreprise. C'est une dégradation invisible : rien ne t'avertit quand la liste passe sous le seuil où elle vaut encore le temps que tu lui consacres.",
        },
        {
          question: "Combien de prospects faut-il vraiment ?",
          answer:
            "Beaucoup moins qu'on ne le croit, dès lors que chaque ligne porte une raison d'être contactée. Une liste courte où tu sais dire pourquoi cette personne et pourquoi maintenant bat une liste longue sans ce pourquoi, pour un temps de travail comparable.",
        },
      ],
    },
    en: {
      title: "Buying a B2B prospect list: what you actually get",
      excerpt:
        "A bought list is a photo taken on a given day, sorted on a criterion that never says whether the person needs you. Here is what you pay for, and what it costs after.",
      description:
        "What a bought list actually contains, why it ages faster than you think, and what changes when you build one from your own calls.",
      ctaTitle: "Build your list instead of buying it",
      ctaText:
        "We pull your targeting criteria from what your clients say, then look for the profiles that genuinely match them. No commitment.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Buying a list is tempting for one simple reason: it is the only way to have a thousand names this afternoon. Everything else takes weeks.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The problem is not that these lists are bad. It is that they answer a question you never asked: they tell you who exists, never who needs you.",
        },
        {
          type: ArticleBlockType.H2,
          text: "What you are actually buying",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A list is a photograph. Someone went through public profiles and company sites on a given date, kept the ones whose job title matched your request, and sold you the result.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Two things follow, and they explain the disappointing results. The photo starts ageing the moment it is taken. And the sorting criterion, the job title, is the weakest one there is: it says what someone does, not what is a problem for them right now.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "A list tells you who exists. It never tells you who has a problem you can solve, nor when.",
        },
        {
          type: ArticleBlockType.H2,
          text: "The three costs you do not see at purchase",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Decay.** People change roles, companies, addresses. A list does not break at once, it degrades every month, and nobody warns you when it drops below the point of being useful.",
            "**No intent.** Nothing in the list says why to write to this person today rather than in a year. So you write the same thing to everyone, which is exactly what makes replies collapse.",
            "**Responsibility.** The moment you use that data you become the controller for how you use it: informing people, allowing them to object, being able to say where the data came from. The seller does not carry that for you.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "In B2B, writing to someone about their professional role does not require prior consent in France. That does not remove the duty to inform them, nor to leave a simple way to object, in every message. A bought list gives you neither of those.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Bought list, built list",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "What a bought list gives you",
            items: [
              "A thousand names, sorted on a job title",
              "No idea what any of them worries about",
              "One identical message for everyone",
              "An origin you cannot explain",
            ],
          },
          good: {
            title: "What a built list gives you",
            items: [
              "Fewer names, chosen on a situation",
              "A reason to write, specific to each",
              "Words taken from your own clients",
              "An origin you own and can justify",
            ],
          },
        },
        {
          type: ArticleBlockType.H2,
          text: "The test to run before sending anything",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Take a row of your list at random and answer one question: why this person, and why today. If the answer is their job title, you do not have a reason to write, you have an address.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "That test kills most bought lists in ten seconds, and it kills plenty of hand-built ones too. It does not say volume is useless; it says volume without a reason to write produces volume of silence.",
        },
        {
          type: ArticleBlockType.H2,
          text: "What you already have, and it is better",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Your calls hold the material no vendor can sell you: the situation your clients were in when they bought, what was blocking them, and the words they used to say it.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "That is where real search criteria come from. Not job titles, but signals: a team that just grew, a tool someone announced dropping, a responsibility that changed. A list sorted on those is shorter, and every row carries its own reason to be contacted.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Criteria drawn from your calls, then the profiles that match",
          text: "Meidly's prospect search builds your targeting criteria from what your clients say, then screens profiles against those criteria rather than against a job title.",
          linkLabel: "Discover prospect search",
          linkPath: ProtectedPaths.PROSPECT_SEARCH,
        },
        {
          type: ArticleBlockType.H2,
          text: "When a bought list still makes sense",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "To map a market you do not know yet, without writing to anyone.",
            "To check the size of a segment before investing time in it.",
            "To find specific companies you have already decided you care about.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "In those three cases the list serves to understand, not to contact. That is the use where it is honest, and nobody sells it that way.",
        },
      ],
      faq: [
        {
          question: "Is buying a B2B prospect list legal?",
          answer:
            "Writing to someone about their professional role does not require prior consent in B2B in France. You must still inform them, say where the data came from, and leave a simple way to object in every message. By using the list you become the controller for how you use it.",
        },
        {
          question: "Why does a bought list get so few replies?",
          answer:
            "Because it is sorted on the job title, which says what a person does and never what is a problem for them. Without a reason to write specific to each one, the message is the same for everyone, and that hurts reply rates far more than address quality does.",
        },
        {
          question: "How fast does a list go stale?",
          answer:
            "It does not expire at once, it wears out continuously, at the pace of role and company changes. The decay is invisible: nothing warns you when the list drops below the point where it is still worth the time you spend on it.",
        },
        {
          question: "How many prospects do you actually need?",
          answer:
            "Far fewer than people think, as long as every row carries a reason to be contacted. A short list where you can say why this person and why now beats a long one without that why, for a comparable amount of work.",
        },
      ],
    },
  },
};
